import React, { useState } from 'react';
import { Box, Collapse, Tooltip } from '@mui/material';
import SourceOutlinedIcon from '@mui/icons-material/SourceOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReportGmailerrorredOutlinedIcon from '@mui/icons-material/ReportGmailerrorredOutlined';
import { ChatProvenanceSource, ChatGrounded } from './chatbotTypes';

interface ChatAnswerTrustProps {
  provenance?: ChatProvenanceSource[];
  grounded?: ChatGrounded | null;
}

// A quiet trust footer under an assistant answer: the run_sql queries behind it (expandable) plus a
// groundedness marker. Advisory only — the server computes both; this just surfaces them. Renders nothing
// when there is nothing auditable to show.
const ChatAnswerTrust = ({ provenance, grounded }: ChatAnswerTrustProps): JSX.Element | null => {
  const [open, setOpen] = useState(false);
  const sources = provenance ?? [];
  const unmatched = grounded?.unmatched ?? [];
  const isPartial = grounded?.status === 'partial' && unmatched.length > 0;
  const isTraced = grounded?.status === 'grounded';
  if (sources.length === 0 && !isPartial && !isTraced) {
    return null;
  }
  return (
    <Box sx={styles.wrap}>
      {sources.length > 0 && (
        <Box
          component="button"
          type="button"
          onClick={() => setOpen((wasOpen) => !wasOpen)}
          sx={styles.chip}
          aria-expanded={open}
        >
          <SourceOutlinedIcon sx={styles.icon} />
          {sources.length} source{sources.length === 1 ? '' : 's'}
        </Box>
      )}
      {isTraced && (
        <Tooltip title={`All ${grounded?.checked} figures trace to data Khora computed this turn`}>
          <Box sx={[styles.status, styles.traced]}>
            <CheckCircleOutlineIcon sx={styles.icon} />
            traced
          </Box>
        </Tooltip>
      )}
      {isPartial && (
        <Tooltip title={`Not traced to this turn's computed data — worth verifying: ${unmatched.join(', ')}`}>
          <Box sx={[styles.status, styles.unverified]}>
            <ReportGmailerrorredOutlinedIcon sx={styles.icon} />
            {unmatched.length} unverified
          </Box>
        </Tooltip>
      )}
      <Collapse in={open} unmountOnExit sx={styles.collapse}>
        <Box sx={styles.sqlList}>
          {sources.map((source, index) => (
            <Box key={index} sx={styles.sql}>
              <code>{source.sql}</code>
              {typeof source.row_count === 'number' && (
                <Box component="span" sx={styles.rowCount}>
                  {' — '}
                  {source.row_count} row{source.row_count === 1 ? '' : 's'}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

const styles = {
  wrap: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, mt: 0.75 },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.25,
    cursor: 'pointer',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 999,
    px: 0.75,
    py: 0.1,
    fontSize: '0.66rem',
    fontFamily: 'monospace',
    color: 'text.secondary',
    background: 'transparent',
  },
  status: { display: 'inline-flex', alignItems: 'center', gap: 0.25, fontSize: '0.66rem', fontFamily: 'monospace' },
  traced: { color: 'success.main' },
  unverified: { color: 'warning.main' },
  icon: { fontSize: 13 },
  collapse: { flexBasis: '100%' },
  sqlList: { mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.4 },
  sql: {
    fontSize: '0.66rem',
    fontFamily: 'monospace',
    bgcolor: 'action.hover',
    borderRadius: 1,
    px: 0.75,
    py: 0.4,
    overflowX: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  rowCount: { opacity: 0.6 },
} as const;

export default ChatAnswerTrust;
